import {
  AloftDate,
  SimilarityFeature,
  SimilarityScoreCharacteristics,
} from '@aloft/toolkit-shared';

/**
 * Formats the per-feature difference between a comp and the subject for display
 * in the Similarity Score breakdown tooltip.
 *
 * Returns undefined when neither value is present (feature excluded from scoring).
 */
export const formatFeatureDifference = (
  feature: SimilarityFeature,
  subjectChars: SimilarityScoreCharacteristics,
  compChars: SimilarityScoreCharacteristics,
  compSaleDate?: AloftDate,
): string | undefined => {
  switch (feature) {
    case 'proximityMiles': {
      const val = compChars.proximityMiles;
      if (val === undefined) return undefined;
      return `${val.toFixed(2)} mi`;
    }

    case 'gla': {
      const subj = subjectChars.gla;
      const comp = compChars.gla;
      if (comp === undefined || subj === undefined) return undefined;
      const diff = Math.round(comp - subj);
      return diff === 0 ? '0 sf' : `${diff > 0 ? '+' : ''}${diff.toLocaleString()} sf`;
    }

    case 'lotSize': {
      const subj = subjectChars.lotSize;
      const comp = compChars.lotSize;
      if (comp === undefined || subj === undefined) return undefined;
      const diff = Math.round(comp - subj);
      return diff === 0 ? '0 sf' : `${diff > 0 ? '+' : ''}${diff.toLocaleString()} sf`;
    }

    case 'actualAge': {
      const subj = subjectChars.actualAge;
      const comp = compChars.actualAge;
      if (comp === undefined || subj === undefined) return undefined;
      const diff = Math.round(comp - subj);
      return diff === 0 ? '0 yrs' : `${diff > 0 ? '+' : ''}${diff} yrs`;
    }

    case 'saleDate': {
      // compChars.saleDate = months elapsed since effective date (0 for subject)
      const months = compChars.saleDate;
      if (months === undefined || !compSaleDate) return undefined;
      const days = Math.round(months * 30.44);
      const monthLabel = compSaleDate.format('MM/yy');
      return `${days} d (${monthLabel})`;
    }

    case 'condition': {
      const subj = subjectChars.condition;
      const comp = compChars.condition;
      if (comp === undefined || subj === undefined) return undefined;
      const diff = comp - subj;
      return diff === 0 ? `C${comp}` : `${diff > 0 ? '+' : ''}${diff} (C${comp})`;
    }

    case 'quality': {
      const subj = subjectChars.quality;
      const comp = compChars.quality;
      if (comp === undefined || subj === undefined) return undefined;
      const diff = comp - subj;
      return diff === 0 ? `Q${comp}` : `${diff > 0 ? '+' : ''}${diff} (Q${comp})`;
    }

    case 'bedroomCount': {
      const subj = subjectChars.bedroomCount;
      const comp = compChars.bedroomCount;
      if (comp === undefined || subj === undefined) return undefined;
      const diff = comp - subj;
      return diff === 0 ? `${comp}` : `${diff > 0 ? '+' : ''}${diff}`;
    }

    case 'fullBathCount': {
      const subj = subjectChars.fullBathCount;
      const comp = compChars.fullBathCount;
      if (comp === undefined || subj === undefined) return undefined;
      const diff = comp - subj;
      return diff === 0 ? `${comp}` : `${diff > 0 ? '+' : ''}${diff}`;
    }

    case 'halfBathCount': {
      const subj = subjectChars.halfBathCount;
      const comp = compChars.halfBathCount;
      if (comp === undefined || subj === undefined) return undefined;
      const diff = comp - subj;
      return diff === 0 ? `${comp}` : `${diff > 0 ? '+' : ''}${diff}`;
    }

    case 'garage': {
      const val = compChars.garage;
      if (val === undefined) return undefined;
      return val ? 'Yes' : 'No';
    }

    case 'basement': {
      const val = compChars.basement;
      if (val === undefined) return undefined;
      return val ? 'Yes' : 'No';
    }

    case 'pool': {
      const val = compChars.pool;
      if (val === undefined) return undefined;
      return val ? 'Yes' : 'No';
    }

    default:
      return undefined;
  }
};
